import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as Sentry from "@sentry/angular";
import { environment } from '../../environments/environment';

export type QRDAEnvironment = 'local' | 'dev' | 'test' | 'prod' | 'vm';

export interface AppConfig {
    BASE_URL: string;
    SITE_KEY: string;
    local_SITE_KEY: string;

    SPLASH_TIMEOUT: number;
    PAGE_TITLE: string;

    INV_SITE_KEY: string;

    PASSWORD_ENCODE_TIMER: number;
    IDLE_TIMEOUT: number;

    ITEMS_PER_PAGE: number;

    PORTAL_HELP_URL: string;
    PORTAL_WHATS_NEW_URL: string;
    PORTAL_HELP_URL_INDIVIDUALS: string;

    PORTAL_BAT_ZIP_FILE: string;
    PORTAL_BAT_ZIP_FILE_EgyptTrust: string;

    PORTAL_WHATS_NEW_URL_INDIVIDUALS: string;

    LOCALHOST_URL: string;

    [key: string]: string | number | boolean;
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
    private _config!: Readonly<AppConfig>;
    private _environment!: QRDAEnvironment;

    private readonly REQUIRED_KEYS = ['BASE_URL'];

    constructor(private http: HttpClient) { }

    /** Load config once during APP_INITIALIZER */
    async load(): Promise<void> {
        console.log('[ConfigService] Loading config...');

        try {
            const screen = 'api-config';
            const headers = { 'content-type': 'application/json' };
            const cfgRes = await firstValueFrom(this.http.get<any>(`assets/json/${screen}.json`, { 'headers': headers }));
            const cfg = cfgRes?.apiConfig;
            console.log('[ConfigService] Raw config loaded:', cfg);
            this.validateConfig(cfg);
            //this.applyDevApiProxyBaseUrl(cfg);
            this._environment = this.detectEnvironment(cfg.BASE_URL);
            if (this.isLocal) {
                console.log('[ConfigService] add local key:', cfg.local_SITE_KEY);
                cfg.SITE_KEY = cfg.local_SITE_KEY;
                cfg.INV_SITE_KEY = cfg.local_SITE_KEY;
            }
            if (this.isVm) {
                console.log('[ConfigService] add VM key:', cfg.VM_SITE_KEY);
                cfg.SITE_KEY = cfg.VM_SITE_KEY;
                cfg.INV_SITE_KEY = cfg.VM_SITE_KEY;
            }

            this._config = Object.freeze({ ...cfg });
            console.log('[ConfigService] Config loaded successfully:');
            console.log('[ConfigService] Detected environment:', this._environment);

            if (this.sentryEnabled) {
                this.activateSentry()
            }
            else {
                console.debug(`[Sentry] Monitoring is not enabled on this ${this._environment}`);
            }
        } catch (err) {
            console.error('[ConfigService] Failed to load config:', err);
            throw err; // fail app bootstrap
        }
    }

    private activateSentry() {
        const client = Sentry.getClient();
        if (client) {
            client.getOptions().enabled = true;
            console.debug('[Sentry] Monitoring enabled via ConfigService');
        }
    }
    get sentryEnabled() {
        return this._environment === 'test'
            || this._environment === 'prod';
    }

    /** Safe during HTTP intercept before `load()` finishes (e.g. fetching api-config.json). */
    get configIfReady(): Readonly<AppConfig> | null {
        return this._config ?? null;
    }

    get config(): Readonly<AppConfig> {
        if (!this._config) {
            throw new Error('ConfigService used before initialization');
        }
        return this._config;
    }

    get environment(): QRDAEnvironment {
        return this._environment;
    }
    get isLocal(): boolean {
        return this._environment === 'local';
    }

    get isDev(): boolean {
        return this._environment === 'dev';
    }

    get isTest(): boolean {
        return this._environment === 'test';
    }

    get isProd(): boolean {
        return this._environment === 'prod';
    }
    get isVm(): boolean {
        return this._environment === 'vm';
    }
    getKey<T extends string | number | boolean>(key: string): T | undefined {
        return this._config[key] as T;
    }

    /** Rewrite api-config BASE_URL to `http://localhost:4200/dev` when using proxy.conf.json. */
    private applyDevApiProxyBaseUrl(cfg: AppConfig): void {
        if (!environment.devApiProxy || environment.production || typeof window === 'undefined') {
            return;
        }
        const origin = window.location.origin.replace(/\/$/, '');
        cfg.BASE_URL = `${origin}/dev`;
        console.log('[ConfigService] devApiProxy → BASE_URL:', cfg.BASE_URL);
    }

    private detectEnvironment(baseUrl: string): QRDAEnvironment {
        let env: QRDAEnvironment;
        if (baseUrl.includes('/api')) {
            env = 'vm';
            return env;
        }
        const host = window.location.hostname;
        const isLocalHost = host === 'localhost' || host === '127.0.0.1';

        if (isLocalHost) {
            env = 'local';
        } else if (baseUrl.includes('/dev')) {
            env = 'dev';
        } else if (baseUrl.includes('/test') || baseUrl.includes('/qa')) {
            env = 'test';
        } else {
            env = 'prod';
        }

        console.log(
            `[ConfigService] host="${host}", baseUrl="${baseUrl}" → environment="${env}"`
        );

        return env;
    }

    private validateConfig(cfg: AppConfig) {
        for (const key of this.REQUIRED_KEYS) {
            if (!(key in cfg)) {
                throw new Error(`[ConfigService] Missing required config key "${key}"`);
            }
            if (typeof cfg[key] !== 'string') {
                throw new Error(
                    `[ConfigService] Config key "${key}" must be a string, got ${typeof cfg[key]}`
                );
            }
        }
    }
}
